import { Alignment, Color, getThemeForKey } from "@mcleod/core";
import { ComponentPropDefinitionUtil, ComponentPropDefinitions } from "../../base/ComponentProps";
import { PropType } from "../../base/PropType";
import { RadioGroupPropDefinitions, RadioGroupProps } from "./RadioGroupProps";

export interface RadioBoxProps extends RadioGroupProps {
    captionAlignment: Alignment.TOP | Alignment.LEFT;
    selectedBackgroundColor: Color;
    selectedTextColor: Color;
    unselectedBackgroundColor: Color;
    unselectedTextColor: Color;
}

export class RadioBoxPropDefinitions {
    private static radioBoxPropsDefs: ComponentPropDefinitions;

    public static getDefinitions(): ComponentPropDefinitions {
        if (this.radioBoxPropsDefs == null) {
            this.radioBoxPropsDefs = {
                ...RadioGroupPropDefinitions.cloneDefs(),
                ...this.buildSimpleProps(),
            };
            this.updateItemsProperty();
            this.adjustEventSignatures();
            ComponentPropDefinitionUtil.populateComponentPropNames(this.radioBoxPropsDefs);
        }
        return this.radioBoxPropsDefs;
    }

    private static buildSimpleProps(): ComponentPropDefinitions {
        return {
            captionAlignment: {
                type: PropType.string,
                dropdownProps: { items: [Alignment.LEFT, Alignment.TOP] },
                defaultValue: Alignment.TOP,
                visibleInMcLeodTailor: true,
                description: "This controls whether the caption appears above or to the left of the options."
            },
            selectedBackgroundColor: {
                type: PropType.color,
                defaultValue: getThemeForKey("radioBoxOption.selectedBackgroundColor"),
                visibleInMcLeodTailor: true,
                description: "This color will be used as the background color of the selected item/label.  The default is the current theme's primary color."
            },
            selectedTextColor: {
                type: PropType.color,
                defaultValue: getThemeForKey("radioBoxOption.selectedTextColor"),
                visibleInMcLeodTailor: true,
                description: "This color will be used as the text color of the selected item/label.  The default is the current theme's 'reverse' primary color."
            },
            unselectedBackgroundColor: {
                type: PropType.color,
                defaultValue: getThemeForKey("radioBoxOption.unselectedBackgroundColor"),
                visibleInMcLeodTailor: true,
                description: "This color will be used as the background color of any unselected item/label.  The default is 'transparent', which allows the color behind the unselected item/label to be visible."
            },
            unselectedTextColor: {
                type: PropType.color,
                defaultValue: getThemeForKey("radioOption.displayValue").color, 
                visibleInMcLeodTailor: true,
                description: "This color will be used as the text color of any unselected item/label.  The default is the current theme's subtle (darker) color."
            }
        };
    }

    private static updateItemsProperty() {
        this.radioBoxPropsDefs.items.type = PropType.radioBoxItem;
        this.radioBoxPropsDefs.items.description =
            "This specifies the list of items that will be represented as radio box items.";
    }

    private static adjustEventSignatures() {
        this.radioBoxPropsDefs.afterSelection.eventSignature = "AfterSelection(event: RadioBoxOptionSelectionEvent)";
        this.radioBoxPropsDefs.beforeSelection.eventSignature = "BeforeSelection(event: RadioBoxOptionSelectionEvent)";
    }
}
